import { useState } from 'react';

type Segment = 'up' | 'down';

/**
 * Stacked positive / negative sentiment bar. Shared by the feature leaderboard
 * (per-account) and the accounts table (portfolio overview). Sentiment follows
 * the vote only, so there is no neutral segment.
 *
 * When `interactive`, each segment is clickable and reveals the underlying counts
 * (positive / negative) below the bar, with the clicked one emphasised.
 * Leave it off inside an already-clickable row to avoid nested buttons.
 */
export function SentimentBar({
  up,
  down,
  total,
  className = '',
  interactive = false,
  onSegmentSelect,
}: {
  up: number;
  down: number;
  total: number;
  className?: string;
  interactive?: boolean;
  /** Called when a segment is clicked (interactive only), e.g. to filter by sentiment. */
  onSegmentSelect?: (kind: Segment) => void;
}) {
  const [active, setActive] = useState<Segment | null>(null);
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  const label = `${up} positive, ${down} negative`;

  const colors: Record<Segment, string> = {
    up: 'var(--chart-1, #14b8a6)',
    down: 'var(--destructive, #ef4444)',
  };

  const segment = (key: Segment, value: number) => {
    const style = { width: `${pct(value)}%`, backgroundColor: colors[key] };
    if (!interactive) return <div key={key} style={style} />;
    const names: Record<Segment, string> = { up: 'positive', down: 'negative' };
    return (
      <button
        key={key}
        type="button"
        aria-label={`${value} ${names[key]}`}
        aria-pressed={active === key}
        onClick={(e) => {
          e.stopPropagation();
          setActive((prev) => (prev === key ? null : key));
          onSegmentSelect?.(key);
        }}
        style={style}
        className="h-full min-w-[2px] cursor-pointer transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    );
  };

  const bar = (
    <div
      className={`flex ${interactive ? 'h-3' : 'h-2.5'} w-full overflow-hidden rounded-full bg-muted ${className}`}
      role={interactive ? 'group' : 'img'}
      aria-label={label}
    >
      {segment('up', up)}
      {segment('down', down)}
    </div>
  );

  if (!interactive) return bar;

  return (
    <div className="w-full">
      {bar}
      {active && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs tabular-nums">
          <Count label="positive" value={up} color="var(--chart-1, #14b8a6)" emphasised={active === 'up'} />
          <Count label="negative" value={down} color="var(--destructive, #ef4444)" emphasised={active === 'down'} />
        </div>
      )}
    </div>
  );
}

function Count({
  label,
  value,
  color,
  emphasised,
}: {
  label: string;
  value: number;
  color: string;
  emphasised: boolean;
}) {
  return (
    <span className={emphasised ? 'font-semibold' : 'text-muted-foreground'} style={emphasised ? { color } : undefined}>
      <span aria-hidden style={{ color }}>
        ●
      </span>{' '}
      {value} {label}
    </span>
  );
}
